import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  ButtonGroup,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CloseIcon from "@mui/icons-material/Close";
import { getBookCategory, getBookAbbreviation } from "../../bible/bibleCategories";

const ChapterMatrixPane = ({
  selectedBook,
  bookChapters = [],
  chapterTitles = {},
  onOpenChapter,
  edition,
  readChapterMap = {},
  highlightsMap = {},
  isMobile = false,
  onClose,
}) => {
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'list'
  const isEn = edition?.id === "en";

  const category = useMemo(() => {
    return getBookCategory(selectedBook, isEn);
  }, [selectedBook, isEn]);

  const abbr = useMemo(() => {
    return getBookAbbreviation(selectedBook);
  }, [selectedBook]);

  // Statistics for this book
  const { readCount, totalCount, firstUnreadChapter } = useMemo(() => {
    const total = bookChapters.length;
    let count = 0;
    let firstUnread = null;

    bookChapters.forEach((ch) => {
      if (readChapterMap[ch.id]) {
        count += 1;
      } else if (!firstUnread) {
        firstUnread = ch;
      }
    });

    return {
      readCount: count,
      totalCount: total,
      firstUnreadChapter: firstUnread || bookChapters[0],
    };
  }, [bookChapters, readChapterMap]);

  if (!selectedBook || bookChapters.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: "center",
          borderRadius: 3,
          border: "1px dashed",
          borderColor: "divider",
          bgcolor: "background.paper",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AutoStoriesIcon sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {isEn ? "Select a book to browse chapters" : "点击左侧书卷开始查阅"}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {isEn
            ? "Click any book from the left catalog to see its chapters and themes"
            : "从左侧书卷目录中选择任意一卷，即可在此浏览章节与主题纲要"}
        </Typography>
      </Paper>
    );
  }

  const percent =
    totalCount > 0 ? Math.round((readCount / totalCount) * 100) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Book Header */}
      <Box
        sx={{
          pb: 2,
          mb: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, minWidth: 0, flexWrap: "nowrap" }}>
              <Chip
                label={abbr}
                size="small"
                color="primary"
                sx={{ fontWeight: 800, fontSize: "0.75rem", height: 22 }}
              />
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: "text.primary",
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  whiteSpace: "nowrap",
                  wordBreak: "keep-all",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedBook}
              </Typography>
              {category && (
                <Chip
                  label={category.shortLabel || category.label}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.7rem",
                    height: 22,
                    color: "text.secondary",
                    borderColor: "divider",
                  }}
                />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary">
              {isEn
                ? `${totalCount} Chapters · ${readCount} Completed (${percent}%)`
                : `全书共 ${totalCount} 章 · 已读 ${readCount} 章 (${percent}%)`}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {!isMobile && (
              <ButtonGroup size="small" variant="outlined" sx={{ mr: 0.5 }}>
                <Button
                  variant={viewMode === "grid" ? "contained" : "outlined"}
                  onClick={() => setViewMode("grid")}
                  sx={{ minWidth: 36, px: 1 }}
                >
                  <Tooltip title={isEn ? "Grid View" : "数字网格"}>
                    <ViewModuleIcon fontSize="small" />
                  </Tooltip>
                </Button>
                <Button
                  variant={viewMode === "list" ? "contained" : "outlined"}
                  onClick={() => setViewMode("list")}
                  sx={{ minWidth: 36, px: 1 }}
                >
                  <Tooltip title={isEn ? "Themes List" : "章节纲要"}>
                    <ViewListIcon fontSize="small" />
                  </Tooltip>
                </Button>
              </ButtonGroup>
            )}

            {onClose && (
              <IconButton size="small" onClick={onClose} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Quick action buttons */}
        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={() => onOpenChapter(bookChapters[0].id)}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              fontSize: "0.82rem",
            }}
          >
            {isEn ? "Start Chapter 1" : "从第 1 章读起"}
          </Button>

          {firstUnreadChapter && firstUnreadChapter.chapterNumber > 1 && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => onOpenChapter(firstUnreadChapter.id)}
              sx={{
                borderRadius: 2,
                fontSize: "0.82rem",
                textTransform: "none",
              }}
            >
              {isEn
                ? `Continue Ch ${firstUnreadChapter.chapterNumber}`
                : `继续读第 ${firstUnreadChapter.chapterNumber} 章`}
            </Button>
          )}
        </Box>
      </Box>

      {/* Chapter View: Grid or List */}
      <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, maxHeight: isMobile ? "60vh" : "680px" }}>
        {viewMode === "grid" ? (
          <Grid container spacing={{ xs: 1, sm: 1.25 }}>
            {bookChapters.map((chapter) => {
              const chNum = chapter.chapterNumber;
              const chTitle = chapterTitles[chapter.id] || "";
              const isRead = Boolean(readChapterMap[chapter.id]);
              const hasHighlight = Boolean(highlightsMap[chapter.id]);

              return (
                <Grid
                  item
                  xs={3}
                  sm={2.4}
                  md={2}
                  lg={1.71}
                  xl={1.5}
                  key={chapter.id}
                >
                  <Tooltip
                    arrow
                    title={
                      chTitle ? (
                        <Box sx={{ p: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, display: "block" }}
                          >
                            {isEn ? `Chapter ${chNum}` : `第 ${chNum} 章`}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            {chTitle}
                          </Typography>
                        </Box>
                      ) : (
                        isEn ? `Chapter ${chNum}` : `第 ${chNum} 章`
                      )
                    }
                  >
                    <Button
                      fullWidth
                      variant={isRead ? "outlined" : "outlined"}
                      onClick={() => onOpenChapter(chapter.id)}
                      sx={{
                        py: { xs: 1, sm: 1.25 },
                        px: 0.5,
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: { xs: 46, sm: 54 },
                        position: "relative",
                        borderColor: isRead ? "success.main" : "divider",
                        bgcolor: isRead
                          ? (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(34, 197, 94, 0.1)"
                                : "rgba(34, 197, 94, 0.05)"
                          : "background.paper",
                        color: isRead ? "success.main" : "text.primary",
                        transition: "all 0.18s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "action.hover",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.95rem", sm: "1.05rem" },
                          lineHeight: 1,
                        }}
                      >
                        {chNum}
                      </Typography>

                      {/* Small badge indicators */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 3,
                          right: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                      >
                        {hasHighlight && (
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: "warning.main",
                            }}
                          />
                        )}
                        {isRead && (
                          <CheckCircleIcon
                            sx={{ fontSize: 11, color: "success.main" }}
                          />
                        )}
                      </Box>
                    </Button>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          /* List View with Full Chapter Themes */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {bookChapters.map((chapter) => {
              const chNum = chapter.chapterNumber;
              const chTitle = chapterTitles[chapter.id] || "";
              const isRead = Boolean(readChapterMap[chapter.id]);

              return (
                <Paper
                  key={chapter.id}
                  elevation={0}
                  onClick={() => onOpenChapter(chapter.id)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: isRead ? "success.light" : "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "action.hover",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isRead
                          ? "success.light"
                          : "action.selected",
                        color: isRead ? "success.contrastText" : "text.primary",
                        fontWeight: 800,
                        fontSize: "0.95rem",
                        flexShrink: 0,
                      }}
                    >
                      {chNum}
                    </Box>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        {isEn ? `Chapter ${chNum}` : `第 ${chNum} 章`}
                      </Typography>
                      {chTitle && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {chTitle}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {isRead && (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      icon={<CheckCircleIcon />}
                      label={isEn ? "Read" : "已读"}
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ChapterMatrixPane;
